import { forwardRef, type Ref, type ReactNode } from 'react';
import { Tooltip } from '../../overlays/Tooltip/Tooltip.js';
import { NavLink, type NavLinkProps } from '../../data-display/NavLink/NavLink.js';
import { useAppShell } from './AppShellContext.js';

export interface AppShellNavLinkProps extends NavLinkProps {
  /**
   * Tooltip label when navbar is in `'icon'` mode. Defaults to the same
   * value as `label` so screen readers + sighted users see the same text.
   */
  iconLabel?: ReactNode;
  /** Override per-instance; otherwise inherits from the AppShell navbar mode. */
  forceMode?: 'icon' | 'compact' | 'full';
}

/**
 * Navbar row that automatically adapts to the current `<AppShell.Navbar>`
 * mode:
 *   • `'icon'`: only the icon renders; label moves into a tooltip
 *   • `'compact'`: icon stacked above a tiny label (no description)
 *   • `'full'`: full icon + label (+ description) row
 */
export const AppShellNavLink = forwardRef(function AppShellNavLink(
  props: AppShellNavLinkProps,
  ref: Ref<HTMLAnchorElement | HTMLButtonElement>
): ReactNode {
  const ctx = useAppShell();
  const mode = props.forceMode ?? ctx.navbarMode;

  const { iconLabel, label, description, leftSection, rightSection, children, className, ...rest } = props;

  if (mode === 'icon') {
    const tip = iconLabel ?? label;
    const node = (
      <NavLink
        ref={ref}
        label={<span className="rr-sr-only">{label}</span>}
        leftSection={leftSection}
        className={['rr-app-shell-navlink', 'rr-app-shell-navlink--icon', className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
    return tip ? <Tooltip label={tip} position="right" offset={10}>{node}</Tooltip> : node;
  }

  if (mode === 'compact') {
    return (
      <NavLink
        ref={ref}
        label={<span className="rr-app-shell-navlink__compact-label">{label}</span>}
        leftSection={leftSection}
        className={['rr-app-shell-navlink', 'rr-app-shell-navlink--compact', className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  }

  // full
  return (
    <NavLink
      ref={ref}
      label={label}
      description={description}
      leftSection={leftSection}
      rightSection={rightSection}
      className={['rr-app-shell-navlink', 'rr-app-shell-navlink--full', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </NavLink>
  );
});
AppShellNavLink.displayName = 'AppShellNavLink';
